import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

/** Department picker mirroring LocationInput: free text with filtered suggestions and
 *  genuine live counts from every source on the board. The filter only commits when a
 *  suggestion is picked (or the field is cleared), typing alone never filters. */
export default function DepartmentInput({ value, onChange, counts = {}, categories = [], testid }) {
  const [text, setText] = useState(value || "");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => { setText(value || ""); }, [value]);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const q = text.toLowerCase();
  // busiest departments first so live counts are immediately useful
  const matches = categories
    .filter((c) => !q || c.toLowerCase().includes(q))
    .sort((a, b) => (counts[b] || 0) - (counts[a] || 0))
    .slice(0, 15);

  const pick = (c) => { setText(c); onChange(c); setOpen(false); };
  const clear = () => { setText(""); onChange(""); setOpen(true); };

  return (
    <div className="relative" ref={ref}>
      <input value={text} data-testid={testid} autoComplete="off"
        onChange={(e) => { setText(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Department"
        className="w-full h-11 px-3 pr-8 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 text-zinc-700" />
      {(text || value) && (
        <button type="button" onClick={clear} aria-label="Clear department"
          data-testid={`${testid}-clear`}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 transition-colors">
          <X className="h-4 w-4" />
        </button>
      )}
      {open && matches.length > 0 && (
        <ul className="absolute z-30 left-0 right-0 top-full mt-1 bg-white border border-zinc-200 rounded-md shadow-lg max-h-64 overflow-y-auto"
          role="listbox" data-testid={`${testid}-options`}>
          {matches.map((c) => (
            <li key={c}>
              <button type="button" role="option" aria-selected={value === c}
                onClick={() => pick(c)}
                data-testid={`${testid}-option-${c.replace(/\W+/g, "-").toLowerCase()}`}
                className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 focus:bg-zinc-50 focus:outline-none flex items-center justify-between gap-2">
                <span className="text-zinc-800">{c}</span>
                <span className="text-xs text-zinc-400 shrink-0">{(counts[c] || 0).toLocaleString("en-GB")} live</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
